// Asset upload utility using Firebase Storage.
//
// Images are uploaded to Firebase Storage under the `assets/` path and stored
// as permanent public URLs. Firebase Authentication (Google Sign-In) is
// required before uploading.
//
// No changes to Firebase Storage rules are needed — uploads use the existing
// `assets/` path covered by `match /assets/{allPaths=**}`.

import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { onAuthStateChanged, signInWithPopup } from 'firebase/auth';
import { app, auth, googleProvider } from '../../../firebase/firebaseApp';

// ── localStorage keys ──────────────────────────────────────────────────────
const ASSET_URLS_KEY = 'df_asset_urls';
const STORAGE_AUTH_KEY = 'df_storage_auth_user';

// ── Auth state tracking ────────────────────────────────────────────────────

let _currentUser = null;
const _authListeners = new Set();

onAuthStateChanged(auth, (user) => {
  _currentUser = user;
  try {
    if (user) {
      localStorage.setItem(
        STORAGE_AUTH_KEY,
        JSON.stringify({ uid: user.uid, email: user.email, displayName: user.displayName })
      );
    } else {
      localStorage.removeItem(STORAGE_AUTH_KEY);
    }
  } catch {
    // ignore localStorage errors
  }
  _authListeners.forEach((cb) => cb(user));
});

/**
 * Subscribe to storage auth state changes.
 * @param {function} callback - called with the Firebase User or null
 * @returns {function} unsubscribe function
 */
export function onStorageAuthChange(callback) {
  _authListeners.add(callback);
  // Fire immediately with current state
  callback(_currentUser);
  return () => _authListeners.delete(callback);
}

/**
 * Returns the currently signed-in storage user, or null.
 */
export function getStorageAuthUser() {
  return _currentUser;
}

/**
 * Returns true if the user is currently signed in to Firebase Storage.
 */
export function isStorageConnected() {
  return !!_currentUser;
}

/**
 * Returns the persisted auth user info from localStorage (for fast UI rendering
 * before the async Firebase Auth state resolves).
 */
export function getPersistedStorageAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_AUTH_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * Sign in with Google to connect to Firebase Storage.
 * @returns {Promise<import('firebase/auth').User>}
 */
export async function connectToStorage() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user;
}

/**
 * Sign out of Firebase to disconnect from storage.
 */
export async function disconnectFromStorage() {
  await auth.signOut();
}

// ── Asset URL map (localStorage) ───────────────────────────────────────────

export function getAssetUrlMap() {
  try {
    return JSON.parse(localStorage.getItem(ASSET_URLS_KEY) || '{}');
  } catch {
    return {};
  }
}

export function getAssetUrl(imageId) {
  if (!imageId) return null;
  return getAssetUrlMap()[imageId] || null;
}

export function setAssetUrl(imageId, url) {
  const map = getAssetUrlMap();
  map[imageId] = url;
  localStorage.setItem(ASSET_URLS_KEY, JSON.stringify(map));
}

// ── Internal helpers ───────────────────────────────────────────────────────

function dataUrlToBlob(dataUrl) {
  const commaIdx = dataUrl.indexOf(',');
  const meta = dataUrl.slice(0, commaIdx);
  const base64 = dataUrl.slice(commaIdx + 1);
  const mime =
    (meta.match(/data:([^;]+);base64/) || [])[1] || 'application/octet-stream';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

function getMimeExtension(dataUrl) {
  const meta = dataUrl.slice(0, dataUrl.indexOf(','));
  const mimeMatch = meta.match(/data:([^;]+);base64/);
  if (!mimeMatch) return 'bin';
  const mime = mimeMatch[1];
  const subtype = mime.includes('/') ? mime.split('/')[1] : '';
  return subtype.split('+')[0] || 'bin';
}

// ── Upload ─────────────────────────────────────────────────────────────────

/**
 * Upload a data-URL image to Firebase Storage and return a permanent public URL.
 *
 * Firebase Authentication (Google Sign-In) is required. Call connectToStorage()
 * first if the user is not yet signed in.
 *
 * @param {string} dataUrl - base64 data URL (e.g. "data:image/png;base64,…")
 * @returns {Promise<string>} permanent public download URL
 */
export async function uploadToStorage(dataUrl) {
  if (!_currentUser) {
    throw new Error(
      'Not signed in to storage. Please connect with Google first.'
    );
  }

  const storage = getStorage(app);
  const ext = getMimeExtension(dataUrl);
  const blob = dataUrlToBlob(dataUrl);
  const mime = blob.type;

  // Unique filename under the user's UID directory
  const randomPart =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '')
      : `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  const filename = `${Date.now()}_${randomPart}.${ext}`;
  const storageRef = ref(storage, `assets/${filename}`);

  const snapshot = await uploadBytes(storageRef, blob, { contentType: mime });
  const downloadUrl = await getDownloadURL(snapshot.ref);
  return downloadUrl;
}

// Alias for compatibility with components that import uploadImage
export const uploadImage = uploadToStorage;
